import { OrgScopedRow } from "../common/types";
import type { VatMode } from "../money/vat";
import { TradeCode, TradeGroup } from "../template/trades";

export const QUOTE_STATUSES = [
  "DRAFT",
  "SENT",
  "NEGOTIATING",
  "WON",
  "LOST",
] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  DRAFT: "작성 중",
  SENT: "발송",
  NEGOTIATING: "조율 중",
  WON: "수주",
  LOST: "이탈",
};

export type QuoteDecision = Extract<QuoteStatus, "WON" | "LOST">;

/**
 * 견적의 종류. **가견적과 상세견적은 같은 표를 쓴다**(제품 원칙(옛 5절)) — 상태·링크·
 * 스냅샷·리비전이 전부 같은 길을 타야 하는데, 표를 나누면 그 길을 두 벌 만들게 된다.
 *
 * `ROUGH` 는 공종·항목이 없다. 면적 × 평당단가 두 칸이 금액의 전부다.
 */
export const QUOTE_KINDS = ["ROUGH", "DETAIL"] as const;
export type QuoteKind = (typeof QUOTE_KINDS)[number];

export const QUOTE_KIND_LABEL: Record<QuoteKind, string> = {
  ROUGH: "가견적",
  DETAIL: "상세견적",
};

// 1평 = 3.305785㎡ — 변환은 area-unit.ts 한 곳.
export { SQM_PER_PYEONG } from "./area-unit";

/**
 * 견적 헤더. 금액 열은 전부 calcQuote 의 결과를 굳힌 것 — 손으로 넣지 않는다.
 * quote_no 는 같은 버전 계열이 공유하고 version 만 올라간다.
 */
export interface Quote extends OrgScopedRow {
  customer_id: string;
  /** `{YYMM}-{조직 내 순번 3자리}` (예: 2609-001). 버전끼리 같다. */
  quote_no: string;
  /** 가견적인가 상세견적인가. 기존 견적은 전부 `DETAIL` 이다. */
  kind: QuoteKind;
  version: number;
  /**
   * 이 행이 몇 번 쓰였는지. 저장할 때마다 1씩 오른다.
   *
   * 편집기의 충돌 감지가 이 숫자를 본다 — **시각으로는 안 된다.** `updated_at`
   * 은 밀리초 해상도라 같은 밀리초 안에 두 번 저장되면 값이 같아지고, 그러면
   * 「아무도 안 건드렸다」 로 읽혀 남의 작업을 덮어쓴다.
   */
  rev: number;
  status: QuoteStatus;
  title: string;
  /** 0.04 = 4%. */
  mgmt_fee_rate: number;
  /** 절사액. null 이면 계산할 때 `gross mod 10000` 으로 채운다. */
  rounding_adjustment: number | null;
  subtotal_basic: number;
  subtotal_style: number;
  mgmt_fee: number;
  gross: number;
  // 항상 부가세 포함 총액 = supply_amount + vat_amount.
  total: number;
  // 판매 단가의 부가세 포함/별도 · 없으면 포함으로 읽는다(저장소가 채우기 전 행).
  vat_mode?: VatMode;
  supply_amount?: number;
  vat_amount?: number;
  conditions: string[];
  extra_notice: string;
  template_id: string | null;
  /**
   * 견적의 면적(평). 가견적은 곱셈 재료, 상세견적은 수량식의 `평` 값이다(0021). 안 적었으면 null.
   *
   * 구간(`customer.area_band` = 「30평대」)이 아니라 **실제 평수**다 — 구간으로는
   * 곱셈을 할 수 없다.
   */
  rough_area_pyeong: number | null;
  /** 면적 입력 원본(m², 0107 · #173). 옛 견적은 null — 화면은 quoteAreaM2 로 평×3.305785 를 보인다. */
  area_m2: number | null;
  // 고정한 공정 표준 판(0106 · #384) — 첫 면적 초안 저장 때 최신 판으로 굳고 다시 안 바뀐다 · 옛 견적은 null.
  process_standard_version_id: string | null;
  /** 가견적의 평당 단가(원). `DETAIL` 이면 null 이다. */
  rough_unit_price: number | null;
  /**
   * 예상 착공일 `YYYY-MM-DD`. null 이면 이 견적은 일정을 쓰지 않는다 —
   * 견적서에서 일정 섹션이 통째로 빠진다.
   */
  expected_start_date: string | null;
  /**
   * 초안을 펼칠 때 **작업일로 셀 날**. 체크 안 한 것은 쉰다.
   *
   * 회사 설정이 아니라 견적에 둔다 — 토요일 작업 여부는 현장·시기마다 갈리고,
   * 설정에 두면 지난 견적의 날짜가 설정을 바꾸는 순간 뜻이 달라진다.
   */
  work_saturday: boolean;
  work_sunday: boolean;
  work_holiday: boolean;
  /**
   * 이 견적의 일정을 펼칠 때 **실제로 쓴 공휴일 날짜들**.
   *
   * null 이면 공휴일을 못 붙인 것이다(키 없음·API 실패) — 견적서가 「공휴일
   * 미반영」 을 그대로 알린다. 빈 배열은 「붙였고 기간 안에 공휴일이 없었다」 다.
   *
   * 결과 날짜만 두지 않고 쓴 날짜를 같이 남기는 이유: 총 작업일은 구간 안의
   * 작업일을 세어 내는데, 공휴일표가 없으면 그 셈이 달라진다. 바깥 API 의 그때
   * 상태에 따라 **이미 보낸 견적서의 숫자가 바뀌면 안 된다.**
   */
  schedule_holidays: string[] | null;
  created_by: string;
  sent_at: string | null;
  decided_at: string | null;
  lost_reason: string | null;
}

export interface QuoteTrade extends OrgScopedRow {
  quote_id: string;
  group: TradeGroup;
  trade: TradeCode;
  /**
   * 공종 표(`trade`)의 행. 코드(`trade`)는 단가표 자연키라 그대로 두고, 참조는
   * id 로 건다 — 회사가 공종 이름을 바꿔도 견적이 따라가야 하기 때문이다.
   *
   * 비지 않는다 — DB 의 외래키가 `deferrable initially deferred` 라, 쓰고 있는
   * 공종을 지우려 하면 커밋 때 막힌다. 화면이 실제로 읽는 값은 위의 `trade`
   * 코드와 `name` 이고 이 id 는 출처를 가리킨다.
   */
  trade_id: string;
  /**
   * 이 견적에서의 공정. 공종의 기본 공정을 복사해 오고, 현장 사정으로 순서가
   * 다르면 여기만 고친다(도배·마루 선후). null 이면 아직 정하지 않았다.
   */
  phase_id: string | null;
  name: string;
  sort_order: number;
  condition_text: string;
  material_amount: number;
  labor_amount: number;
  amount: number;
}

/**
 * 견적의 공정 한 줄 — **언제·며칠**.
 *
 * 줄은 사람이 만들지 않는다. 견적의 공종이 물고 있는 `phase_id` 에서 뽑아 세운다.
 *
 * `duration_days` 는 초안을 펼칠 때 쓰는 입력값이고, `start_date`·`end_date` 는
 * 펼친 결과다. 둘을 같이 들고 있는 이유는 **초안을 다시 만들 수 있어야** 하기
 * 때문이다 — 날짜만 남기면 착공일이 바뀌었을 때 며칠짜리였는지 알 길이 없다.
 *
 * 날짜가 null 이면 아직 안 펼친 것이다.
 */
export interface QuotePhasePlan extends OrgScopedRow {
  quote_id: string;
  /**
   * 공정 표의 행. 공종이 `trade`(코드)와 `trade_id` 를 함께 드는 것과 같은 이유다 —
   * 참조는 id 로 걸고, 화면이 실제로 읽고 쓰는 값은 **코드**다. 회사가 공정 이름을
   * 바꿔도 견적이 따라가야 한다.
   */
  phase_id: string;
  /** 불변 코드. 편집기가 공정 줄을 맞추는 열쇠다. */
  phase_code: string;
  /** 작업일 수. 0 이면 안 쓰는 공정이라 견적서에서 빠진다. */
  duration_days: number;
  start_date: string | null;
  end_date: string | null;
  sort_order: number;
  note: string;
  // 면적 초안의 근거(0106 · #175) — 고른 시공법 코드 · 수량(m²) · 인원 · 일수(0.5 단위) · 손으로만 친 줄은 전부 null.
  standard_item_code: string | null;
  quantity_m2: number | null;
  crew_size: number | null;
  draft_days: number | null;
}

// 견적 실제 원가(`0101`) — 견적 버전·공종 코드마다 한 줄 · 부가세 빼고 · 사내 전용(스냅샷·고객 화면에 안 싣는다).
export interface QuoteActualCost extends OrgScopedRow {
  quote_id: string;
  trade: string;
  amount: number;
  memo: string | null;
  updated_by: string | null;
}

// 실제 원가 저장 한 줄 — id 는 앱이 만든다 · amount 가 null 이면 그 공종 줄을 지운다.
export interface QuoteActualCostSave {
  id: string;
  trade: string;
  amount: number | null;
  memo: string | null;
}

/**
 * 견적 항목. 자재·노무 2축이고, parent_id 가 있으면 SET 부모의 자식이다.
 * 부모는 자기 단가를 쓰지 않고 자식 합을 금액으로 갖는다.
 */
export interface QuoteItem extends OrgScopedRow {
  quote_id: string;
  quote_trade_id: string;
  parent_id: string | null;
  sort_order: number;
  /** 부위. 「벽·천장·몰딩」 같은 자유 문자열이다. */
  scope: string;
  /**
   * 공간. 조직 코드값(`codes.space`)의 `code` 다. null 이면 안 정한 것.
   *
   * `scope` 와 다른 축이다 — 부위는 「벽」, 공간은 「욕실」. 고객이 묻는
   * 「욕실만 얼마」 는 이 열로만 뽑을 수 있다.
   */
  space_code: string | null;
  item: string;
  unit: string;
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
  // 원가 단가(부가세 빼고) · null/없음 = 미입력 · 사내 전용(스냅샷에 안 싣는다).
  cost_unit_price?: number | null;
  // 넣을 때의 적용 마진(만분율) · null/없음 = 원가 없이 넣은 줄 · 사내 전용(스냅샷에 안 싣는다).
  base_margin_bp?: number | null;
  material_amount: number;
  labor_amount: number;
  amount: number;
  note: string;
  /** 수량을 만든 식(`평*2.5`). 사내 전용 — 고객 화면·스냅샷에는 안 싣고, `qty` 와 어긋나면 `qty` 가 이긴다. */
  qty_expr: string | null;
}

/** 화면이 한 번에 받는 견적 한 벌. */
export interface QuoteDetail {
  quote: Quote;
  trades: QuoteTrade[];
  items: QuoteItem[];
  /** 공정 일정. 안 쓰는 견적은 빈 배열이다. */
  plans: QuotePhasePlan[];
}

export interface QuoteShare extends OrgScopedRow {
  quote_id: string;
  token: string;
  /** 고객 휴대폰 뒷 4자리. 열람 인증에 쓴다. */
  last4: string;
  expires_at: string;
  viewed_at: string | null;
  view_count: number;
  revoked_at: string | null;
}

/** 고객이 보는 화면의 항목. 내부 메모·담당자·예산 같은 내부 열은 들어가지 않는다. */
export interface SnapshotItem {
  scope: string;
  space_code: string | null;
  /** 발송 시점의 공간 표기. 나중에 라벨을 고쳐도 고객이 본 것은 그대로다. */
  space_label: string | null;
  item: string;
  unit: string;
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
  material_amount: number;
  labor_amount: number;
  amount: number;
  note: string;
  children: SnapshotItem[];
}

export interface SnapshotTrade {
  group: TradeGroup;
  trade: TradeCode;
  name: string;
  sort_order: number;
  condition_text: string;
  material_amount: number;
  labor_amount: number;
  amount: number;
  items: SnapshotItem[];
}

/** 발송 시점에 굳힌 공정 한 줄. 고객이 본 날짜는 이후 무슨 일이 있어도 안 변한다. */
export interface SnapshotPhase {
  /** 그때의 공정 표기. 나중에 이름을 고쳐도 고객이 본 것은 그대로다. */
  name: string;
  /** 이 공정에 묶인 공종 이름들. 「무슨 작업인지」를 따로 적지 않게 한다. */
  trades: string[];
  start_date: string;
  end_date: string;
}

export interface SnapshotSchedule {
  start_date: string;
  end_date: string;
  /** 공정 구간에 든 작업일의 가짓수. 겹친 날은 한 번만 센다. */
  total_workdays: number;
  /** false 면 공휴일표 없이 만든 일정이다. 견적서에 그대로 알린다. */
  holidays_applied: boolean;
  phases: SnapshotPhase[];
}

/** 고객이 받는 개산서 한 장. 가견적에만 있다. */
export interface SnapshotRough {
  area_pyeong: number;
  unit_price: number;
  /** 면적 × 평당단가. 발송 시점에 굳은 값이다. */
  amount: number;
}

export interface SnapshotData {
  org: { name: string; tel: string; address: string };
  customer: { name: string; site_address: string };
  quote: {
    quote_no: string;
    version: number;
    title: string;
    mgmt_fee_rate: number;
    subtotal_basic: number;
    subtotal_style: number;
    mgmt_fee: number;
    gross: number;
    rounding_adjustment: number;
    total: number;
    // 0098 전에 발송한 스냅샷엔 세 칸이 없다 — 방식은 `snapshotVatMode` 로 읽는다(없으면 포함).
    vat_mode?: VatMode;
    supply_amount?: number;
    vat_amount?: number;
    conditions: string[];
    extra_notice: string;
    sent_at: string;
  };
  trades: SnapshotTrade[];
  /**
   * 가견적의 3칸. **옵셔널이다** — 상세견적에는 없다. 있으면 고객 화면이
   * 3부가 아니라 개산서 한 장으로 나간다.
   */
  rough?: SnapshotRough;
  /**
   * 시공일정. **옵셔널이다** — 이 기능 전에 발송한 스냅샷에는 없고, 일정을 안 쓴
   * 견적에도 없다. 없으면 견적서에서 일정 섹션이 통째로 빠진다.
   */
  schedule?: SnapshotSchedule;
}

// 스냅샷의 부가세 방식 — 칸이 없는 옛 스냅샷은 포함으로 읽는다.
export const snapshotVatMode = (data: Pick<SnapshotData, "quote">): VatMode => data.quote.vat_mode ?? "INCL";

/** 발송 시점에 굳은 견적. 이후 초안이 바뀌어도 고객이 본 화면은 이것이다. */
export interface QuoteSnapshot extends OrgScopedRow {
  quote_id: string;
  version: number;
  data: SnapshotData;
}

/**
 * 편집 중인 견적. **로그인 계정마다 한 줄**이다.
 *
 * 자동저장이 `quote_trade`·`quote_item` 을 직접 덮으면 두 사람이 같은 견적을
 * 열었을 때 마지막 저장이 이긴다 — 상대가 쓴 것이 소리 없이 사라진다. 그래서
 * 편집 중 내용은 여기 따로 쌓고, 「저장」 을 눌렀을 때만 본문 표로 넘어간다.
 *
 * `data` 가 jsonb 인 이유: 편집 중 데이터는 조회 대상이 아니고 통째로 덮어쓴다.
 * 행으로 쪼개면 몇 초마다 delete + insert 가 돈다.
 */
export interface QuoteDraft extends OrgScopedRow {
  quote_id: string;
  /** 로그인 계정. 이 두 값이 자연키다. */
  member_id: string;
  data: QuoteDraftData;
  /**
   * 편집기를 열었을 때의 `quote.rev`. 저장하려는 순간의 값과 다르면 그 사이에
   * 누가 저장한 것이라, 덮어쓰기 전에 사람에게 묻는다.
   *
   * 시각이 아니라 번호인 이유는 `Quote.rev` 주석에 있다 — 시각으로 두면 같은
   * 밀리초 안의 두 저장을 놓친다.
   */
  base_rev: number;
}

/**
 * 초안 본문. 모양은 편집기가 서버로 보내는 것과 같다 — 저장할 때 그대로
 * `saveDraft` 에 넘겨야 하므로 여기서 따로 정규화하지 않는다.
 */
export type QuoteDraftData = Record<string, unknown>;

export const ACTIVITY_TARGETS = [
  "customer",
  "quote",
  "share",
  "org",
  "member",
  "invite",
  "template",
  "portfolio",
  "contract",
  "project",
] as const;
export type ActivityTarget = (typeof ACTIVITY_TARGETS)[number];

export interface Activity extends OrgScopedRow {
  actor_id: string | null;
  action: string;
  target_type: ActivityTarget;
  target_id: string;
  summary: string;
  meta: Record<string, string>;
}
